import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";

import { ModernBatchGrid } from "@/components/modern-batch-grid";
import { PageHeader } from "@/components/page-header";
import { StudentLanding } from "@/components/student/student-landing";
import { Skeleton } from "@/components/ui/skeleton";
import { currentRole, pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Post-signin landing. Role-branched (matches portal-ui's `/`):
 *   - students → StudentLanding (personalized greeting + their batch's
 *     next exam + recent results + performance trend + batch/news).
 *   - everyone else → batches grid.
 *
 * Signin redirects here, so the route MUST stay at /dashboard and
 * stay reachable.
 */

interface BatchRecord {
  id: string;
  name: string;
  exam_year?: string;
  start_date?: string;
  end_date?: string;
}

interface TeacherRecord {
  id: string;
  name: string;
  batches: string[];
  login?: string;
}

function batchesQuery(authId: string | undefined) {
  return {
    queryKey: ["batches", "list", "dashboard", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("batch").getList<BatchRecord>(1, 24, {
        sort: "-created",
        skipTotal: true,
        fields: "id,name,exam_year,start_date,end_date",
      }),
  };
}

function teachersQuery(authId: string | undefined) {
  return {
    queryKey: ["teachers", "list", "dashboard", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("teacher").getList<TeacherRecord>(1, 50, {
        sort: "-created",
        skipTotal: true,
        fields: "id,name,batches,login",
      }),
  };
}

export const Route = createFileRoute("/_app/dashboard")({
  loader: ({ context }) => {
    // Non-student loaders prefetch the batches grid data. Students will
    // hydrate their own queries inside StudentLanding via useSuspenseQuery
    // — prefetching them at the route-loader level would require knowing
    // the student record before the route renders, which is itself a
    // query. Keeping that lookup inside the component is simpler and
    // adds at most one round-trip on first paint.
    if (currentRole() === "student") return Promise.resolve();
    const authId = pb.authStore.model?.id;
    return Promise.all([
      context.queryClient.ensureQueryData(batchesQuery(authId)),
      context.queryClient.ensureQueryData(teachersQuery(authId)),
    ]);
  },
  component: DashboardPage,
});

function DashboardPage() {
  const isStudent = currentRole() === "student";
  useDocumentTitle(isStudent ? "Home" : "Batches");

  if (isStudent) {
    return (
      <Suspense fallback={<StudentLandingSkeleton />}>
        <StudentLanding />
      </Suspense>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Cohorts"
        title="Batches"
        meta="Browse cohorts, faculty and active timelines."
      />
      <Suspense fallback={<GridSkeleton />}>
        <BatchesGrid />
      </Suspense>
    </>
  );
}

function BatchesGrid() {
  const authId = pb.authStore.model?.id;
  const { data: batches } = useSuspenseQuery(batchesQuery(authId));
  const { data: teachers } = useSuspenseQuery(teachersQuery(authId));
  return <ModernBatchGrid batches={batches.items} teachers={teachers.items} />;
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: stable skeleton placeholder
        <Skeleton key={i} className="h-44 w-full rounded-2xl" />
      ))}
    </div>
  );
}

function StudentLandingSkeleton() {
  return (
    <div className="grid auto-rows-[minmax(140px,auto)] grid-cols-1 gap-3 md:grid-cols-12 md:gap-4">
      <Skeleton className="h-44 rounded-2xl md:col-span-12" />
      <Skeleton className="rounded-2xl md:col-span-8 md:row-span-2 md:h-full md:min-h-[360px]" />
      <Skeleton className="rounded-2xl md:col-span-4 md:row-span-2 md:h-full md:min-h-[360px]" />
      <Skeleton className="rounded-2xl md:col-span-7 md:row-span-2 md:h-full md:min-h-[360px]" />
      <Skeleton className="rounded-2xl md:col-span-5 md:row-span-2 md:h-full md:min-h-[360px]" />
      <Skeleton className="h-40 rounded-2xl md:col-span-12" />
    </div>
  );
}
